import { useEffect, useState } from "react";
import { message } from "antd";
import {
  ApartmentOutlined,
  DownOutlined,
  FileTextOutlined,
  UpOutlined,
} from "@ant-design/icons";

import LoadingSkeleton from "../../../components/CustomComponents/LoadingSkeleton";
import { useAppSelector } from "../../../hooks/redux";
import { parseServerDate } from "../../utils/serverDate";
import {
  openWorkflowOutput,
  resolveLatestWorkflowRun,
  type WorkflowChatContext,
} from "../workflowChat";
import styles from "./styles.module.scss";

/**
 * Resolve the workflow's latest run/output once per chat. ``null`` while
 * loading; the composer waits so the first message carries full context.
 */
export const useResolvedWorkflow = (
  workflow: WorkflowChatContext | null | undefined
): WorkflowChatContext | null => {
  const clinicId = useAppSelector((state) => state.clinic.userClinic?.id);
  const userEmail = useAppSelector((state) => state.auth.user?.email);
  const [resolved, setResolved] = useState<WorkflowChatContext | null>(null);

  useEffect(() => {
    setResolved(null);
    if (!workflow) return;
    let active = true;
    resolveLatestWorkflowRun(workflow, {
      clinicId: clinicId ? Number(clinicId) : undefined,
      userEmail,
    })
      .then((latest) => {
        if (active) setResolved({ ...workflow, ...latest });
      })
      .catch(() => {
        // No run to show is still a valid chat about the workflow.
        if (active) setResolved({ ...workflow, run: null });
      });
    return () => {
      active = false;
    };
  }, [workflow, clinicId, userEmail]);

  return resolved;
};

const formatWhen = (iso: string | null, timeZone?: string | null) => {
  if (!iso) return null;
  const date = parseServerDate(iso);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return date.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      ...(timeZone ? { timeZone } : {}),
    });
  } catch {
    return date.toLocaleString();
  }
};

/** Head of a workflow chat: which workflow, its latest run, and the output
 *  (files, or the Monday briefing email). */
const WorkflowOutputCard = ({
  workflow,
}: {
  workflow: WorkflowChatContext | null;
}) => {
  const clinic = useAppSelector((state) => state.clinic.userClinic);
  const [expanded, setExpanded] = useState(false);

  if (!workflow) {
    return (
      <div className={styles.workflowCard}>
        <LoadingSkeleton
          ariaLabel="Loading latest output"
          rows={2}
          variant="lines"
        />
      </div>
    );
  }

  const run = workflow.run;
  const when = formatWhen(run?.finishedAt ?? null, clinic?.timezone);
  const open = async (index: number) => {
    if (!run) return;
    try {
      const ok = await openWorkflowOutput(
        workflow,
        run,
        run.outputs[index],
        clinic?.id ? Number(clinic.id) : undefined
      );
      if (!ok) message.error("Couldn’t open this workflow output.");
    } catch {
      message.error("Couldn’t open this workflow output.");
    }
  };

  return (
    <div className={styles.workflowCard}>
      <div className={styles.workflowCardHead}>
        <span className={styles.workflowCardIcon} aria-hidden>
          <ApartmentOutlined />
        </span>
        <span className={styles.workflowCardTitle}>
          <strong>{workflow.name}</strong>
          <span>
            {run
              ? when
                ? `Latest output, ${when}`
                : "Latest output"
              : "No output yet"}
          </span>
        </span>
      </div>

      {run && run.outputs.length > 0 && (
        <div className={styles.workflowFiles}>
          {run.outputs.map((output, index) => (
            <button
              key={output.fileId}
              type="button"
              onClick={() => void open(index)}
            >
              <FileTextOutlined />
              {output.name}
            </button>
          ))}
        </div>
      )}

      {workflow.email && (
        <div className={styles.workflowEmail}>
          <span className={styles.workflowEmailSubject}>
            {workflow.email.subject}
          </span>
          <p
            className={
              expanded ? styles.workflowEmailBodyOpen : styles.workflowEmailBody
            }
          >
            {workflow.email.bodyText}
          </p>
          <button
            type="button"
            className={styles.workflowEmailToggle}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? (
              <>
                Show less <UpOutlined />
              </>
            ) : (
              <>
                Show full email <DownOutlined />
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default WorkflowOutputCard;
